import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import i18next from 'i18next';
import { Redirect } from 'react-router-dom';

import AssetTypeForm from './asset_type_form';
import { PageTitle } from '../PageTitle';
import { AssetTypesService } from '../../services';

import { OpSenseToastContainer, toast } from '../Toast';
import { Button, Modal } from "react-bootstrap";

const AssetTypeEdit = ({ match, me, location }) => {
  const [assetType, setAssetType] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [assetTypeUpdated, setAssetTypeUpdated] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assetTypeToDelete, setAssetTypeToDelete] = useState(null);
  const [assetTypeDeleted, setAssetTypeDeleted] = useState(false);

  useEffect(() => {
    setHasError(false);
    AssetTypesService.get(match.params.assetTypeId).then((response) => {
      setAssetType(response.assetType);
    }).catch((error) => {
      console.error(error);
      setHasError(true);
      toast.error(i18next.t("messages.could-not-load-asset-type-edit"));
    });
  }, [match]);

  useEffect(() => {
    if(assetTypeToDelete) {
      AssetTypesService.delete(assetTypeToDelete.id).then((_) => {
          setShowDeleteModal(false);
          setAssetTypeToDelete(null);
          setAssetTypeDeleted(true);
      }).catch((error) => {
          console.error(error);
          setHasError(true);
          toast.error(i18next.t("messages.could-not-delete-asset-type"));
      });
    }
  }, [assetTypeToDelete]);

  const handleSubmit = useCallback(({name, requiresFloorplan, defaultTags, defaultAreaTag, measures, assetTypeMetadataTypes}) => {
    let allDefaultTags = defaultTags ? defaultTags.map((tag) => ({ id: tag.value })) : [];
    if (defaultAreaTag) {
      allDefaultTags.push({ id: defaultAreaTag.value });
    }

    if (!measures) {
      toast.error(i18next.t("messages.please-select-at-least-one-measure"));
      throw new SubmissionError({measures: i18next.t("messages.please-select-at-least-one-measure")});
    }

    const updatedAssetType = {
      id: assetType.id,
      enterprise: {id: me.enterprise.id},
      name: name,
      requiresFloorplan: requiresFloorplan,
      defaultTags: allDefaultTags,
      measures: measures.map((measure) => ({
          name: measure.name,
          displayOrder: measure.displayOrder,
          deviceDataClassification: { id: measure.measureType.deviceDataClassification.id }
        })
      ),
      assetTypeMetadataTypes: assetTypeMetadataTypes ? assetTypeMetadataTypes.map((metadataType) => ({ 
          name: metadataType.name,
          stringValueFlag: metadataType.valueType === 'string',
          numericValueFlag: metadataType.valueType === 'numeric'
        })
      ) : []
    };

    AssetTypesService.update(assetType.id, updatedAssetType).then((_) => {
      setAssetTypeUpdated(true);
    }).catch(error => {
      console.error(error);
      toast.error(error.message);
      setAssetTypeUpdated(false);
    });
  }, [assetType]);

  if (assetTypeUpdated) {
    return <Redirect to={{ pathname: '/admin/asset-types', state: { 
        from: location,
        successMessage: i18next.t('messages.asset-type-updated') 
      } 
    }}/>
  } else if (assetTypeDeleted) {
    return <Redirect to={{ pathname: '/admin/asset-types', state: { 
        from: location,
        successMessage: i18next.t('messages.asset-type-deleted') 
      } 
    }}/>
  } else {
    return assetType || hasError ? (
        <div>
          <OpSenseToastContainer />

          <div className="row">
            <PageTitle title={i18next.t('pages.asset-types.edit-asset-type')} backLinkWithParams={{ link: { pathname: "/admin/asset-types" } }} />
          </div>

          <AssetTypeForm 
            onSubmit={handleSubmit} 
            backLinkWithParams={{ link: { pathname: "/admin/asset-types" } }} 
            assetType={assetType} 
            onDelete={() => setShowDeleteModal(true)}
          />

          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>{i18next.t('pages.asset-types.delete-asset-type', {name: assetType.name})}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {i18next.t('messages.are-you-sure-delete')}
            </Modal.Body>
            <Modal.Footer>
              <a className="btn btn-danger" onClick={() => setAssetTypeToDelete(assetType)}>{i18next.t('delete')}</a>
              <Button onClick={() => setShowDeleteModal(false)}>{i18next.t('form-labels.cancel')}</Button>
            </Modal.Footer>
          </Modal>
        </div>
    ) : (
        <div className="loader">{i18next.t("loading")}</div>
    );
  }
}

export default connect(state => ({me: state.user.me}))(AssetTypeEdit);
